import jwtDecode from 'jwt-decode'
import { defineStore } from 'pinia'
import type { IApiInstance } from '~/plugins/api'
import type { EpisodeOfCarePolicies, PatientPolicies, PractitionerPolicies } from '~/repository/types/auth/policies'
import type { Role } from '~/repository/types/auth/role'
import { OwnerType, type ContactPoint } from '~/repository/types/contact-point'
import type { Practitioner } from '~/repository/types/practitioner/practitioner'

export type IUser = {
  firebaseAccessToken: null
  displayName: string | null
  phoneNumber: string | null
  photoURL: string | null
  uid: string | null
  roles: string[]
  permissions: string[]
  loginUserType: string | null
  profession: string | null
  survey: any | null
  signInSource: string | null
  policies: PatientPolicies | EpisodeOfCarePolicies | PractitionerPolicies | null
  isLoginProgress: boolean
  practitioner: Practitioner | null
  tokenExpire: boolean
  contactPoints: ContactPoint[]
}

export const useUserStore = defineStore('user', {
  state: (): IUser => {
    return {
      firebaseAccessToken: null,
      displayName: null,
      phoneNumber: null,
      photoURL: null,
      isLoginProgress: false,
      loginUserType: '',
      uid: null,
      roles: [] as Role[],
      permissions: [] as string[],
      profession: '',
      survey: null,
      signInSource: null,
      policies: null,
      practitioner: null,
      tokenExpire: false,
      contactPoints: [],
    }
  },
  getters: {
    isUserLoged: (state) => state.firebaseAccessToken !== null,
    user: (state) => ({
      uid: state.uid,
      displayName: state.displayName,
      phoneNumber: state.phoneNumber,
      photoURL: state.photoURL,
      profession: state.profession,
      contactPoints: state.contactPoints,
    }),
    token: (state) => state.firebaseAccessToken,
    isSurveyAdministrator: (state) => state.roles.includes('surveyAdministrator'),
    isPractitionerAdministrator: (state) => state.roles.includes('practitionerAdministrator'),
    isAdministrator: (state) => state.roles.includes('administrator'),
    isAdministratorV2: (state) => state.roles.includes('ADMIN_SUPER'),
    isDoctor: (state) => state.profession === 'doctor',
    userPolicies: (state) => state.policies,
    isPractitioner: (state): Practitioner | undefined => {
      const { getPractitionerById } = usePractitionersStore()
      return getPractitionerById(state.uid ?? '')
    },
    isTokenExpire: (state) => state.tokenExpire,
  },
  actions: {
    /**
     * An action that stores the user data and its Firebase JWT for centralized usage.
     * The user should be authenticated through Firebase SDK before using this storage.
     *
     * @param {object} payload - Paylaod containing user data to store aswell as its Firebase accessToken.
     */
    async save(user: any, firebaseAccessToken: any, source = 'web', type = 'email') {
      // Decode JWT token to get more user data
      const currentUserId = this.user.uid
      const userData = jwtDecode(firebaseAccessToken) as any
      this.loginUserType = userData.type
      const { survey, practitioner, authenticationFactors } = userData
      this.updateUser(user)
      this.survey = survey
      this.firebaseAccessToken = firebaseAccessToken
      this.signInSource = source

      this.tokenExpire = authenticationFactors?.passwordStatus === 'expired' ? true : false
      let rememberMe = localStorage.getItem('rememberMe')
      if (rememberMe) {
        if (rememberMe === 'true') {
          const provider = user.providerData.filter((p: any) => p.providerId === userData?.firebase?.sign_in_provider)?.[0]
          if (provider?.providerId === 'oidc.psc') {
            user.rppsValue = provider.uid.split(':')[2]
            this.setRememberMe(user, 'RPPS')
          } else if (provider?.providerId === 'phone') {
            this.setRememberMe(user, 'phone')
          } else {
            this.setRememberMe(user, type)
          }
        }
      }
      this.loadRoles()

      if (userData.personId && currentUserId !== user.uid) {
        const api: IApiInstance = useNuxtApp().$api as IApiInstance
        const contactPoints = await api.contactPoints.getV2({ type: OwnerType.person, id: userData.personId })
        this.contactPoints = contactPoints

        this.practitioner = (await api.practitioners.getV2(this.uid as string)) as Practitioner
        this.profession = practitioner?.profession

        this.loadPolicies()
      }
    },
    setRememberMe(userData: any, type: string) {
      let value = userData.email
      if (type === 'RPPS') {
        value = userData.rppsValue
      } else if (type === 'phone') {
        value = userData.phoneNumber?.replace(/^\+\d{2}/, '0')
      }
      const userInfo = {
        id: userData.uid,
        name: userData.displayName,
        type: type || 'email',
        email: value,
      }
      const rememberedUsers = JSON.parse(localStorage.getItem('rememberUser') || '[]')
      if (rememberedUsers.length) {
        const existingUserIndex = rememberedUsers.findIndex((user: any) => user.id === userInfo.id)
        if (existingUserIndex !== -1) {
          rememberedUsers[existingUserIndex] = userInfo
        } else {
          rememberedUsers.push(userInfo)
        }
        localStorage.setItem('rememberUser', JSON.stringify(rememberedUsers))
      } else {
        localStorage.setItem('rememberUser', JSON.stringify([userInfo]))
      }
    },

    updateUser(payload: IUser) {
      if (payload.uid) this.uid = payload.uid
      if (payload.displayName) this.displayName = payload.displayName
      if (payload.phoneNumber) this.phoneNumber = payload.phoneNumber
      if (payload.photoURL) this.photoURL = payload.photoURL
      if (payload.profession) this.profession = payload.profession
    },

    /**
     * An action that is designed to be equivalent to the classic flow of Firebase authentication.
     * Used exclusively for the webview entrypoint which sends the accessToken through which the SDK
     * cannot authenticate the user again.
     *
     * @param {object} context - VueX store context containing commit helper for mutations.
     * @param {string} token - Firebase JWT accessToken/idToken which contains the user data.
     */
    saveFromToken(token: any) {
      /*
       * PS: This method could be replaced with a HTTP call to
       *     POST https://identitytoolkit.googleapis.com/v1/accounts:lookup?key={{NUXT_ENV_FIREBASE_APIKEY}}
       *     in order to fetch the same data as the Firebase SDK.
       *     It won't solve the SDK authentication problem though.
       *     There is no SDK method to authenticate with an accessToken only.
       */

      // Decode JWT token to get user data
      // NB: As we are not authenticated in Firebase SDK no this.$fire calls can be made with this user
      const currentUser = jwtDecode(token) as any
      // Transpose JWT schema to the schema from Firebase user lookup
      // NB: There is no photoURL in the JWT schema
      this.uid = currentUser.user_id
      this.practitioner = currentUser?.practitioner
      this.displayName = currentUser.name
      this.phoneNumber = currentUser.phone_number
      this.profession = currentUser?.practitioner?.profession
      this.firebaseAccessToken = token
      this.signInSource = 'embed'
      this.profession = currentUser.practitioner?.profession
      this.tokenExpire = currentUser?.authenticationFactors?.passwordStatus === 'expired' ? true : false
      this.loadRoles()
    },

    /**
     * An action that sets the state from persisted Firebase SDK state. Usually IndexedDB.
     *
     * @param {object} payload - Firebase persisted state. Containing authenticated user data.
     */
    async setFirebaseStateOnLoad(authUser: any) {
      // Don't try to reauthenticate the user using the cookies if there are none found
      if (!authUser) return

      // Reauthenticate the user using the state found in cookies
      this.save(authUser, await authUser.getIdToken(true), 'local')
    },

    /**
     * An action that sets the user policies
     *
     */
    async loadPolicies() {
      const api: IApiInstance = useNuxtApp().$api as IApiInstance
      this.policies = await api.auth.getPolicies('practitioner')
    },

    /**
     * An action that refreshes the user roles from the API.
     * This is useful when roles might have changed and need to be updated in the store.
     */
    async loadRoles() {
      const api: IApiInstance = useNuxtApp().$api as IApiInstance
      if (this.uid && this.firebaseAccessToken) {
        // let roleItems
        // if (this.loginUserType === 'user') {
        //   roleItems = await api.users.getUserRoleV2(this.uid)
        //   this.roles = roleItems?.items.map((item: any) => item.role)
        //   let permissions
        //   permissions = await api.users.getPermissions()
        //   this.permissions = permissions?.items.map((item: any) => item.permission)
        // } else {
        //   roleItems = await api.users.getUserRole(this.uid)
        //   this.roles = roleItems?.roles.map((item: any) => item)
        // }
        const userData = jwtDecode(this.firebaseAccessToken as string) as any
        this.roles = userData?.roles || []
        if (this.loginUserType === 'user') {
          let permissions = await api.users.getPermissions()
          this.permissions = permissions?.items.map((item: any) => item.permission)
        }
      }
    },

    /**
     * An action to clear the user data from the storage, should be used upon signing out the user.
     *
     * @param {object} context - VueX store context containing commit helper for mutations.
     */
    clear() {
      this.$reset()
    },
  },
})
